import { useId, type ReactNode } from 'react';
import { ApiError } from '../api/client';

export function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: ReactNode;
  error?: string | null;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy = [help !== undefined ? helpId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {help !== undefined && (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      )}
      {children({
        id,
        ...(describedBy !== '' ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true } : {}),
      })}
      {error && (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}

/** Explains a failed mutation, with specific guidance for lost updates and access changes. */
export function MutationError({ error, conflictMessage }: { error: unknown; conflictMessage?: string }) {
  if (error === null || error === undefined) return null;
  let message = 'The change was not saved. Try again.';
  if (error instanceof ApiError) {
    if (error.isConflict) {
      message =
        conflictMessage ??
        'Someone changed this since you opened it. Your text is kept below; the latest version has been loaded — check it before sending again.';
    } else if (error.isForbidden) {
      message = 'Your account is not allowed to make this change. Access is checked on every request.';
    } else if (error.isUnauthenticated) {
      message = 'Your session has ended. Sign in again, then retry.';
    } else {
      message = error.message;
    }
  }
  return (
    <p className="form-error" role="alert">
      {message}
    </p>
  );
}
